import React, { useState, useEffect, useRef, useMemo } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { MobileTimePicker, LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { DemoItem } from '@mui/x-date-pickers/internals/demo';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs'; // Added dayjs import for parsing time strings
import './componentreg.css';
import { useForm } from 'react-hook-form';
import { CustomDaySelect, convertTo24Hour } from '../Inputfield/inputfield';
import Swal from 'sweetalert2';
import { Tooltip } from '@mui/material';
import { customerbasedshift, shiftadd } from '../../Services/app/masterservice';
import { cleanCustomerId, isPmiCustomer } from '../../Services/app/operatorservice';
import { useUserRole } from '../../Shared/hooks/useUserRole';
import { filterPlantsForUser } from '../../Shared/utils/machineGroupAccess';

// Helper function to parse time strings robustly
// It attempts to parse the string as HH:mm:ss first by prepending a dummy date.
// If that fails, it tries to parse as a full date/time string.
// This ensures the MobileTimePicker receives a valid Dayjs object,
// and the 'format' prop on the picker will ensure only the time is displayed.

const parseTime = (timeString) => {
  if (!timeString) return null;

  let parsed = null;

  // Attempt 1: Parse as HH:mm:ss by prepending a dummy date.
  // This is the most common format for the dialogData times (e.g., "08:00:00").
  parsed = dayjs(`2000-01-01 ${timeString}`, 'YYYY-MM-DD HH:mm:ss');
  if (parsed.isValid()) {
    // Convert from GMT to IST (add 5 hours 30 minutes)
    return parsed.add(5, 'hour').add(30, 'minute');
  }

  // Attempt 2: If the above fails, try parsing as a full date/time string
  // (e.g., "Tue10jun2025 18:30:00 GMT"). 
  parsed = dayjs(timeString);
  if (parsed.isValid()) {
    // Check if the string contains 'GMT' to determine if conversion is needed
    if (timeString.includes('GMT')) {
      // Convert from GMT to IST (add 5 hours 30 minutes)
      return parsed.add(5, 'hour').add(30, 'minute');
    } else {
      // If no GMT indication, assume it's already in local time
      return parsed;
    }
  }

  // Attempt 3: Try parsing with explicit GMT timezone
  parsed = dayjs(timeString + ' GMT');
  if (parsed.isValid()) {
    // Convert from GMT to IST (add 5 hours 30 minutes)
    return parsed.add(5, 'hour').add(30, 'minute');
  }

  // If still not valid, return null
  return null;
};
const parseTime24 = (timeString) => {
  if (!timeString) return null;

  try {
    // Parse using 24-hour format
    const parsed = dayjs(timeString, 'HH:mm:ss', true);

    if (parsed.isValid()) {
      const hours = parsed.hour();
      const minutes = parsed.minute();
      const seconds = parsed.second();

      // Validate 24-hour format bounds
      if (
        hours >= 0 && hours <= 23 &&
        minutes >= 0 && minutes <= 59 &&
        seconds >= 0 && seconds <= 59
      ) {
        // Return the dayjs object instead of formatted string
        return parsed;
      }
    }

    return null;
  } catch (error) {
    console.error('Error parsing time:', error);
    return null;
  }
};


export default function ComponentEdit({ open, handleClose, handleAdd, dialogOpenCount, datasource, setDatasource, customerId, dialogData }) {
  console.log('datasource', datasource);
  console.log('dialogData', dialogData);
  const customDaySelectRef = useRef();
  const dialogBackgroundColor = dialogOpenCount === 0 ? '#f7f7f7' : '#ededed';
  const [shiftsmodule, setShiftsmodule] = useState([]);
  const modeSelectRef = useRef();
  const isCustomerId = cleanCustomerId(customerId) === window._env_.BIOTEK_CUSTOMER_ID;

  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        if (modeSelectRef.current) {
          // For Material-UI Select, we need to trigger focus on the input element
          const selectElement = modeSelectRef.current;
          if (selectElement) {
            selectElement.focus();
            // If the above doesn't work, try clicking to open the dropdown
            selectElement.click();
          }
        }
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [open]);
  const { register, handleSubmit, formState: { errors }, trigger, setValue, reset } = useForm({
    shouldFocusError: true,
    mode: 'onBlur'
  });

const [operationNumberError, setOperationNumberError] = useState('');

  const getOperationNumberDuplicateError = (form) => {
    const norm = (val) => (val ?? '').toString().trim().toLowerCase();
    const list = Array.isArray(datasource) ? datasource : [];
    if (isCustomerId) {
      if (!norm(form.operation_number)) return '';
      const editingOp = dialogData?.__editingOperation;
      const componentId = dialogData?.id?.$oid || dialogData?.id;
      const comp = list.find((c) => (c.id?.$oid || c.id) === componentId);
      if (!comp) return '';
      const dup = (comp.operations || []).some(
        (op) => op.id !== editingOp?.id && norm(op.operation_number) === norm(form.operation_number)
      );
      return dup ? 'This Component Number already has this Operation Number' : '';
    }
    if (cleanCustomerId(customerId) === window._env_.GPLAST_CUSTOMER_ID) {
      if (!norm(form.operation_number)) return '';
      const editId = dialogData?.id?.$oid || dialogData?.id;
      const dup = list.some((s) => {
        const sid = s.id?.$oid || s.id;
        if (sid === editId) return false;
        return (
          norm(s.component_name) === norm(form.component_name) &&
          norm(s.item_code) === norm(form.item_code) &&
          norm(s.process_name) === norm(form.process_name) &&
          norm(s.operation_number) === norm(form.operation_number) &&
          norm(s.plant_code) === norm(form.plant_code)
        );
      });
      return dup ? 'Component with same Item Code and Process Name already exists' : '';
    }
    return '';
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    const nextForm = { ...shiftForm, [name]: value };
    setShiftForm(nextForm);
    setValue(name, value); // Update the form state in react-hook-form
    trigger(name); // Trigger validation for this field
  if (['operation_number', 'component_number', 'component_name', 'item_code', 'process_name'].includes(name)) {
      setOperationNumberError(getOperationNumberDuplicateError(nextForm));
    }
  };

  // Super Admin only: multi-select Plant checkboxes. Plants already carrying
  // this component start checked; unchecking one deletes just that plant's
  // record on save, checking a new one creates a record for it.
  const handlePlantCodesChange = (event) => {
    const { value } = event.target;
    let nextValues = typeof value === 'string' ? value.split(',') : value;

    if (Array.isArray(nextValues) && nextValues.includes('all')) {
      const allPlantCodes = visiblePlantOptions.map((p) => p.code).filter(Boolean);
      const currentlyAllSelected =
        Array.isArray(shiftForm.plant_codes) && shiftForm.plant_codes.length === allPlantCodes.length;
      nextValues = currentlyAllSelected ? [] : allPlantCodes;
    }

    setShiftForm((prev) => ({ ...prev, plant_codes: nextValues }));
    setValue('plant_codes', nextValues);
    trigger('plant_codes');
  };

  // Operation Name must not contain spaces.
  const handleOperationNameChange = (event) => {
    const { name, value } = event.target;
    const noSpaceValue = value.replace(/\s/g, '');
    setShiftForm((prev) => ({ ...prev, [name]: noSpaceValue }));
    setValue(name, noSpaceValue);
    trigger(name);
  };
  const [processGroupOptions, setProcessGroupOptions] = useState([]);
  const defaultShiftForm = useMemo(() => ({
    component_name: '',
    component_number: '',
    operation_number: '',
    operation_name: '',
    operation_type: '',
    factorval: '',
    factor: '',
    jobcard: '',
    drawingcode: '',
    cycle_time: null,
    handling_time: null,
    setupTime: null,
    item_code: '',
    process_name: '',
    plant_code: '',
    plant_codes: [],
  }), []);
  const [plantOptions, setPlantOptions] = useState([]);
  const showPlantField = isPmiCustomer(customerId) && plantOptions.length > 0;
  const { userRole, isSuperAdmin } = useUserRole();
  const normalizeComponentField = (val) => (val || '').toString().trim().toLowerCase();
  const userPlantCodes = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('userDetails') || '{}').plantAccess || [];
    } catch {
      return [];
    }
  }, []);
  // Non-Super-Admins only ever see the plant(s) assigned to them.
  const visiblePlantOptions = useMemo(
    () => filterPlantsForUser(plantOptions, userRole, userPlantCodes),
    [plantOptions, userRole, userPlantCodes]
  );

  const fetchPlantGroups = async () => {
    try {
      if (!isPmiCustomer(customerId)) {
        setPlantOptions([]);
        return;
      }
      const data = await customerbasedshift(customerId, 'plantgroups');
      const allPlantGroups = data?.[0]?.value || [];
      setPlantOptions(allPlantGroups.map((p) => ({ code: p.code, name: p.name || p.code })));
    } catch (error) {
      console.error('Error fetching plant groups:', error);
      setPlantOptions([]);
    }
  };

  useEffect(() => {
    fetchPlantGroups();
  }, [customerId]);

  const handleTimeChange = (name, value) => {
    setShiftForm((prevShiftForm) => ({
      ...prevShiftForm,
      [name]: value,
    }));
    setValue(name, value); // Update the form state in react-hook-form
    trigger(name); // Trigger validation for this field

  };
  const [shiftForm, setShiftForm] = useState(defaultShiftForm);
  const onSubmit = async (data) => {

    try {
      setOperationNumberError('');
      const timeFields = [
        { key: "cycle_time", label: "Cycle Time" },
        { key: "handling_time", label: "Handling Time" },
        { key: "setupTime", label: "Setup Time" },
      ];
      for (const { key, label } of timeFields) {
        const value = data[key];
        if (!value || !dayjs(value).isValid()) {
          handleClose();
          Swal.fire({
            icon: "error",
            title: "Invalid Input",
            text: `${label} has an invalid or incomplete time format.`,
          });
          return;
        }
        if (key === "cycle_time" && dayjs(value).format("HH:mm:ss") === "00:00:00") {
          handleClose();
          Swal.fire({
            icon: "error",
            title: "Invalid Input",
            text: "Cycle Time must not be 00:00:00.",
          });
          return;
        }
      }
    if (isCustomerId) {
        const safeFormat = (v) => dayjs(v).format('HH:mm:ss');
        const normalizeVal = (val) => (val || '').trim().toLowerCase();
        const editingOp = dialogData?.__editingOperation;
        const componentId = dialogData?.id?.$oid || dialogData?.id;

        const key = 'component';
        const currentDataResponse = await customerbasedshift(customerId, key);
        const allComponents = currentDataResponse?.[0]?.value || [];

        const targetComponent = allComponents.find(
          (comp) => (comp.id?.$oid || comp.id) === componentId
        );
        if (targetComponent) {
          const duplicateOp = (targetComponent.operations || []).find(
            (op) =>
              op.id !== editingOp?.id &&
              normalizeVal(op.operation_number) === normalizeVal(data.operation_number)
          );
          if (duplicateOp) {
         setOperationNumberError('This Component Number already has this Operation Number');
            Swal.fire(
              'Duplicate entry',
              'This Component Number already has this Operation Number',
              'error'
            );
            return;
          }
        }

        const opFields = {
          operation_name: data.operation_name,
          operation_number: data.operation_number,
          cycle_time: safeFormat(data.cycle_time),
          handling_time: safeFormat(data.handling_time),
          setupTime: safeFormat(data.setupTime),
        };

        const updatedComponents = allComponents.map((comp) => {
          const compId = comp.id?.$oid || comp.id;
          if (compId !== componentId) return comp;
          const existingOps = comp.operations || [];
          // Editing an existing operation updates it in place. Editing a
          // component that has no operations yet (opened from the component
          // row) has no editingOp, so add its first operation instead.
          const operations = editingOp
            ? existingOps.map((op) =>
                op.id === editingOp.id ? { ...op, ...opFields } : op
              )
            : [...existingOps, { id: Math.random().toString(36).substr(2, 9), ...opFields }];
          return {
            ...comp,
            factor: '',
            factorval: '',
            operations,
          };
        });

        const formData = {
          component: updatedComponents,
          lastUpdateTs: Date.now(),
        };
        const response = await shiftadd(formData, customerId, 'SERVER_SCOPE');
        Swal.fire(response.msg || 'Updated Successfully');
        handleClose();
        reset(defaultShiftForm);
        return;
      }

      const startTimeString = shiftForm.cycle_time.format('hh:mm:ss A');
      const endTimeString = shiftForm.handling_time.format('hh:mm:ss A');
      const breakTimeString = shiftForm.setupTime.format('hh:mm:ss A'); // Changed to 'hh:mm:ss A' for consistency

      const cycle_time = convertTo24Hour(startTimeString);
      const handling_time = convertTo24Hour(endTimeString);
      const setupTime = convertTo24Hour(breakTimeString);

      console.log('Shift start time:', cycle_time); // Should print "08:30:00" or similar
      console.log('Shift end time:', handling_time);     // Should print "17:45:00" or similar
      console.log('Break time:', setupTime);       // Should print "12:00:00" or similar
      const selectedModule = shiftsmodule.find(option => option.value === data.factor);
      const moduleLabel = selectedModule ? selectedModule.label : '';


      // Find the end day label based on the selected value
      // Get the ID of the shift being edited from dialogData.
      // dialogData is expected to be available in this component's scope,
      // typically passed as a prop or derived from context/state when the dialog opens for editing.
      const shiftIdToEdit = dialogData?.id;

      // Assuming 'datasource' is an array containing existing shift data.
      // If 'datasource' is not an array or is undefined, initialize as an empty array.
      let existingShifts = Array.isArray(datasource) ? [...datasource] : [];
      const isGplast = cleanCustomerId(customerId) === window._env_.GPLAST_CUSTOMER_ID;
      const normalize = (val) => (val || '').trim().toLowerCase();
      const plantCode = showPlantField ? (data.plant_code || undefined) : undefined;
      if (showPlantField && isSuperAdmin) {
        // ===============================
        // 🏭 SUPER ADMIN MULTI-PLANT SYNC
        // Checkboxes drive plant-wise records for this component: kept plants
        // are updated in place, newly checked plants get a new record, and
        // unchecked plants have their record for this component deleted.
        // ===============================
        const getId = (val) => (typeof val === 'object' && val !== null ? val.$oid : val);

        const isOriginalMatch = isGplast
          ? (item) =>
              normalize(item.component_name) === normalize(dialogData?.component_name) &&
              normalize(item.item_code) === normalize(dialogData?.item_code) &&
              normalize(item.process_name) === normalize(dialogData?.process_name) &&
              normalize(item.operation_number) === normalize(dialogData?.operation_number)
          : (item) => normalize(item.component_number) === normalize(dialogData?.component_number);

        const siblingRecords = existingShifts.filter(isOriginalMatch);
        const siblingIds = siblingRecords.map((item) => getId(item.id));
        const selectedPlantCodes = [...new Set((shiftForm.plant_codes || []).filter(Boolean))];

        if (selectedPlantCodes.length === 0) {
          handleClose();
          Swal.fire('Error', 'Select at least one plant.', 'error');
          return;
        }

        const isNewMatch = isGplast
          ? (item) =>
              normalize(item.component_name) === normalize(data.component_name) &&
              normalize(item.item_code) === normalize(data.item_code) &&
              normalize(item.process_name) === normalize(data.process_name) &&
              normalize(item.operation_number) === normalize(data.operation_number)
          : (item) => normalize(item.component_number) === normalize(data.component_number);

        // Block only when a selected plant already has a *different*
        // component matching the new values (not one of our own siblings).
        const conflictingPlants = selectedPlantCodes.filter((pc) => {
          const match = existingShifts.find(
            (item) => isNewMatch(item) && (item.plant_code || undefined) === pc
          );
          return match && !siblingIds.includes(getId(match.id));
        });

        if (conflictingPlants.length > 0) {
          handleClose();
          const plantNames = conflictingPlants.map(
            (pc) => plantOptions.find((p) => p.code === pc)?.name || pc
          );
          const message = isGplast
            ? `Component with same Item Code and Process Name already exists for plant(s): ${plantNames.join(', ')}`
            : `Component Number already exists for plant(s): ${plantNames.join(', ')}`;
          Swal.fire('Duplicate entry', message, 'error');
          return;
        }

        // Drop all of this component's existing plant records, then rebuild
        // from the checked plants (kept ones preserve their id).
        let nextList = existingShifts.filter((item) => !siblingIds.includes(getId(item.id)));

        let addedCount = 0;
        selectedPlantCodes.forEach((pc) => {
          const existingSibling = siblingRecords.find((s) => (s.plant_code || undefined) === pc);
          if (!existingSibling) addedCount += 1;
          nextList.push({
            id: existingSibling ? existingSibling.id : Math.random().toString(36).substr(2, 9),
            component_name: data.component_name,
            component_number: data.component_number,
            operation_number: data.operation_number,
            operation_name: data.operation_name,
            operation_type: data.operation_type,
            factorval: data.factorval,
            factor: moduleLabel,
            jobcard: data.jobcard,
            drawingcode: data.drawingcode,
            cycle_time,
            handling_time,
            setupTime,
            ...(isGplast && {
              item_code: data.item_code,
              process_name: data.process_name,
            }),
            plant_code: pc,
          });
        });
        const removedCount = siblingRecords.length - (selectedPlantCodes.length - addedCount);

        const formData = { component: nextList, lastUpdateTs: Date.now() };
        const response = await shiftadd(formData, customerId, 'SERVER_SCOPE');

        if (response.msg) {
          Swal.fire(response.msg);
        } else {
          const parts = [];
          if (addedCount > 0) parts.push(`${addedCount} plant(s) added`);
          if (removedCount > 0) parts.push(`${removedCount} plant(s) removed`);
          Swal.fire(parts.length > 0 ? `Updated Successfully (${parts.join(', ')})` : 'Updated Successfully');
        }

        handleClose();
        reset(defaultShiftForm);
        return;
      }

      if (isGplast) {
        const duplicate = existingShifts.find((shift) => {
          const itemId =
            typeof shift.id === "object" && shift.id !== null ? shift.id.$oid : shift.id;
          const targetId =
            typeof shiftIdToEdit === "object" && shiftIdToEdit !== null
              ? shiftIdToEdit.$oid
              : shiftIdToEdit;
          if (itemId === targetId) return false;
          return (
            normalize(shift.component_name) === normalize(data.component_name) &&
            normalize(shift.item_code) === normalize(data.item_code) &&
            normalize(shift.process_name) === normalize(data.process_name) &&
            normalize(shift.operation_number) === normalize(data.operation_number) &&
            (shift.plant_code || undefined) === plantCode
          );
        });

        if (duplicate) {
       setOperationNumberError("Component with same Item Code and Process Name already exists");
          Swal.fire(
            "Duplicate entry",
            "Component with same Item Code and Process Name already exists",
            "error"
          );
          return;
        }
      }
      let existingShiftIndex = -1;
      let existingIdStructure = null; // To preserve the original ID structure (e.g., { $oid: "..." } or string)

      if (shiftIdToEdit) {
        // Find the index of the shift to be updated based on its ID.
        // Handle both string IDs and object IDs (like { $oid: "..." }) as per the concept.
        existingShiftIndex = existingShifts.findIndex(item => {
          const itemId = typeof item.id === 'object' && item.id !== null ? item.id.$oid : item.id;
          const targetId = typeof shiftIdToEdit === 'object' && shiftIdToEdit !== null ? shiftIdToEdit.$oid : shiftIdToEdit;
          return itemId === targetId;
        });

        if (existingShiftIndex !== -1) {
          // If the shift is found, preserve its original ID structure.
          existingIdStructure = existingShifts[existingShiftIndex].id;
        }
      }

      if (existingShiftIndex === -1) {
        // If the shift to edit was not found, it's an error in an edit context.
        console.error('Shift not found for ID:', shiftIdToEdit, 'Cannot update.');
        Swal.fire('Error', 'Shift not found for update. Please try again.', 'error');
        handleClose(); // Close the dialog
        reset(defaultShiftForm); // Reset form state
        return; // Stop the submission process
      }

      // This object represents the updated shift data.
      // Use the preserved ID structure for the updated shift.
      const updatedShiftData = {
        id: existingIdStructure,
        component_name: data.component_name,
        component_number: data.component_number,
        operation_number: data.operation_number,
        operation_name: data.operation_name,
        operation_type: data.operation_type,
        factorval: data.factorval,
        factor: moduleLabel,
        jobcard: data.jobcard,
        drawingcode: data.drawingcode,
        cycle_time: cycle_time,
        handling_time: handling_time,
        setupTime: setupTime,
        ...(cleanCustomerId(customerId) === window._env_.GPLAST_CUSTOMER_ID && {
          item_code: data.item_code,
          process_name: data.process_name,
        }),
        ...(showPlantField && {
          plant_code: data.plant_code,
        }),
      };

      // Update the existing shift in the array with the new data.
      existingShifts[existingShiftIndex] = updatedShiftData;
      console.log('Shift updated at index:', existingShiftIndex, 'with data:', updatedShiftData);

      console.log('existingShifts after update:', existingShifts);
      // const isDuplicate = existingShifts.some(
      //   shift =>
      //     shift.component_name.trim().toLowerCase() === data.component_name.trim().toLowerCase() &&
      //     shift.component_number.trim().toLowerCase() === data.component_number.trim().toLowerCase()
      // );

      // if (isDuplicate) {
      //   handleClose();
      //   Swal.fire("Duplicate entry", "Component Name and Number already exist", "error");
      //   return;
      // }
      // Prepare the final payload to be sent to the API.
      // This structure aligns with the existing `formData` definition in this file,
      // where 'allShift' contains the updated list of shifts.
      const formData = {
        component: existingShifts,
        lastUpdateTs: Date.now()
      };

      console.log('Submitted shift data:', JSON.stringify(formData));
      console.log('customerId', customerId);
      const scope = 'SERVER_SCOPE';
      // Make the API call

      const response = await shiftadd(formData, customerId, scope);
      console.log('Shift Updated response:', response);

      if (response.msg) {
        Swal.fire(response.msg);
      } else {
        Swal.fire("Updated Successfully");
      }

      handleClose();
      reset(defaultShiftForm);
    } catch (error) {

      handleClose();
      reset(defaultShiftForm);
      console.error('Error submitting shift data:', error);
      Swal.fire('Error submitting shift data: ' + error.message);
    }
  };

  const fetchProcessGroups = async () => {
    const key = 'processgroups';
    try {
      const data = await customerbasedshift(customerId, key);
      const allProcessGroups = data[0]?.value || [];
      const mappedGroups = allProcessGroups.map((item) => ({
        value: item.groupName,
        label: item.groupName,
      }));
      setProcessGroupOptions(mappedGroups);
    } catch (error) {
      console.error("Error fetching reason groups:", error);
      setProcessGroupOptions([]);
    }
  };

  useEffect(() => {
    // Initialize shiftsmodule when component mounts
    const fallbackOptions = [
      { value: 'Multiplication Factor', label: 'Multiplication Factor' },
      { value: 'Divide Factor', label: 'Divide Factor' },
    ];
    setShiftsmodule(fallbackOptions);
  }, []); // Empty dependency array - runs only once
  useEffect(() => {
    setOperationNumberError('');
    if (!open) {
      reset(defaultShiftForm);
      setShiftForm(defaultShiftForm);
    } else {
      if (cleanCustomerId(customerId) === window._env_.GPLAST_CUSTOMER_ID) {
        fetchProcessGroups();
      }
      if (dialogData) {

     const editingOp = dialogData.__editingOperation;

        // Convert 24-hour time strings to Dayjs objects for 12-hour display
        const initialFormState = {
          // Use the helper function to parse 24-hour times
          cycle_time: parseTime24(isCustomerId ? editingOp?.cycle_time : dialogData.cycle_time),
          handling_time: parseTime24(isCustomerId ? editingOp?.handling_time : dialogData.handling_time),
          setupTime: parseTime24(isCustomerId ? editingOp?.setupTime : dialogData.setupTime),
          component_name: dialogData.component_name || '',
          component_number: dialogData.component_number || '',
          operation_number: isCustomerId
            ? (editingOp?.operation_number || '')
            : (dialogData.operation_number ? Number(dialogData.operation_number) : ''),
          operation_name: isCustomerId ? (editingOp?.operation_name || '') : (dialogData.operation_name || ''),
          operation_type: dialogData.operation_type || '',
          factorval: dialogData.factorval ? Number(dialogData.factorval) : '',
          factor: dialogData.factor || '',
          jobcard: dialogData.jobcard || '',
          drawingcode: dialogData.drawingcode || '',
          item_code: dialogData.item_code || '',
          process_name: dialogData.process_name || '',
          plant_code: dialogData.plant_code || '',
          plant_codes: (() => {
            if (!showPlantField || !isSuperAdmin) return [];
            const isGplast = cleanCustomerId(customerId) === window._env_.GPLAST_CUSTOMER_ID;
            const isSiblingOfDialogData = isGplast
              ? (item) =>
                  normalizeComponentField(item.component_name) === normalizeComponentField(dialogData.component_name) &&
                  normalizeComponentField(item.item_code) === normalizeComponentField(dialogData.item_code) &&
                  normalizeComponentField(item.process_name) === normalizeComponentField(dialogData.process_name) &&
                  normalizeComponentField(item.operation_number) === normalizeComponentField(dialogData.operation_number)
              : (item) => normalizeComponentField(item.component_number) === normalizeComponentField(dialogData.component_number);
            return (Array.isArray(datasource) ? datasource : [])
              .filter(isSiblingOfDialogData)
              .map((item) => item.plant_code)
              .filter(Boolean);
          })(),
        };

        // Find the correct module value if shiftsmodule is already loaded
        if (shiftsmodule.length > 0 && dialogData.module) {
          const selectedModuleOption = shiftsmodule.find(option => option.label === dialogData.module);
          if (selectedModuleOption) {
            initialFormState.module = selectedModuleOption.value;
          }
        }

        setShiftForm(initialFormState);
        reset(initialFormState);
      } else {
        setShiftForm(defaultShiftForm);
        reset(defaultShiftForm);
      }
    }
  }, [open, reset, defaultShiftForm, dialogData, shiftsmodule, datasource, showPlantField, isSuperAdmin, customerId]);

  // Non-Super-Admins with exactly one assigned plant don't need to pick —
  // select it for them automatically (e.g. a legacy record with no plant yet).
  useEffect(() => {
    if (!open || !showPlantField || isSuperAdmin) return;
    if (visiblePlantOptions.length === 1 && !shiftForm.plant_code) {
      const onlyCode = visiblePlantOptions[0].code;
      setShiftForm((prev) => ({ ...prev, plant_code: onlyCode }));
      setValue('plant_code', onlyCode);
      trigger('plant_code');
    }
  }, [open, showPlantField, isSuperAdmin, visiblePlantOptions, shiftForm.plant_code, setValue, trigger]);

  // Rest of the component remains the same...
  // (keeping all the existing code for getShiftsAdddata, handleFormChange, handleTimeChange, onSubmit, and the JSX return)

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="300px" PaperProps={{ style: { backgroundColor: dialogBackgroundColor } }}>
      {/* Dialog content remains the same, but update the MobileTimePicker components */}
      <DialogTitle style={{ color: 'black' }}>Edit Component </DialogTitle>
      <div className="close_modal">
        <Tooltip title="Close">
          <IconButton aria-label="close" onClick={handleClose} style={{ backgroundColor: '#ffffff' }}>
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </div>
      <div className="machinedialog">
        <div className="filter_sec">
          <form onSubmit={handleSubmit(onSubmit)} className="form_sec shift_form" autoComplete="off">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <div className="form_sec_fields">
                {/* Component Name Field */}
                <div className={`form_field ${errors.component_name ? 'error-outline' : ''}`}>
                  <TextField
                    {...register("component_name", {
                      required: "Component Name is required",
                      maxLength: {
                        value: 100,
                        message: "Maximum length is 100 characters"
                      }
                    })}
                    label="Component Name"
                    type="text"
                    name="component_name"
                    value={shiftForm.component_name}
                    onChange={handleFormChange}
                    error={!!errors.component_name}
                    inputProps={{ maxLength: 100 }}
                    disabled
                    InputLabelProps={{
                      required: true,
                      sx: {
                        color: 'black',
                        '&.Mui-focused': {
                          color: 'orange',
                        },
                      },
                    }}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: 'black',
                        },
                        '&:hover fieldset': {
                          borderColor: 'black',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'orange',
                        },
                        '& .MuiOutlinedInput-input': {
                          color: 'black',
                        },
                        '&.Mui-focused .MuiOutlinedInput-input': {
                          caretColor: 'orange',
                        },
                        '&::placeholder': {
                          color: 'black',
                          opacity: 1,
                        },
                      },
                    }}
                  />
                  {errors.component_name && <div className="mat-error">{errors.component_name.message}</div>}
                </div>

                {/* Component Number Field */}
                {(cleanCustomerId(customerId) != window._env_.GPLAST_CUSTOMER_ID) && (
                  <div className={`form_field ${errors.component_number ? 'error-outline' : ''}`}>
                    <TextField
                      {...register("component_number", {
                        required: "Component Number is required",
                        maxLength: {
                          value: 100,
                          message: "Maximum length is 100 characters"
                        }
                      })}
                      label="Component Number"
                      type="text"
                      disabled
                      name="component_number"
                      value={shiftForm.component_number}
                      onChange={handleFormChange}
                      error={!!errors.component_number}
                      inputProps={{ maxLength: 100, min: 1 }}
                      InputLabelProps={{
                        required: true,
                        sx: {
                          color: 'black',
                          '&.Mui-focused': {
                            color: 'orange',
                          },
                        },
                      }}
                      fullWidth
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: 'black',
                          },
                          '&:hover fieldset': {
                            borderColor: 'black',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: 'orange',
                          },
                          '& .MuiOutlinedInput-input': {
                            color: 'black',
                          },
                          '&.Mui-focused .MuiOutlinedInput-input': {
                            caretColor: 'orange',
                          },
                          '&::placeholder': {
                            color: 'black',
                            opacity: 1,
                          },
                        },
                      }}
                    />
                    {errors.component_number && (
                      <div className="mat-error">{errors.component_number.message}</div>
                    )}
                  </div>
                )}

                {showPlantField && (isSuperAdmin ? (
                  <div className={`form_field ${errors.plant_codes ? 'error-outline' : ''}`}>
                    <CustomDaySelect
                      {...register("plant_codes", {
                        validate: (value) => (value && value.length > 0) || "Select at least one plant",
                      })}
                      name="plant_codes"
                      value={shiftForm.plant_codes}
                      onChange={handlePlantCodesChange}
                      label="Select Plant(s)"
                      required={true}
                      multiple
                      showSelectAll={visiblePlantOptions.length > 1}
                      options={visiblePlantOptions.map((p) => ({ value: p.code, label: p.name }))}
                      error={!!errors.plant_codes}
                    />
                    {errors.plant_codes && <div className="mat-error">Select at least one plant</div>}
                  </div>
                ) : (
                  <div className={`form_field ${errors.plant_code ? 'error-outline' : ''}`}>
                    <CustomDaySelect
                      {...register("plant_code", { required: "Plant is required" })}
                      onBlur={() => trigger('plant_code')}
                      name="plant_code"
                      value={shiftForm.plant_code}
                      onChange={handleFormChange}
                      label="Select Plant"
                      required={true}
                      options={visiblePlantOptions.map((p) => ({ value: p.code, label: p.name }))}
                      error={!!errors.plant_code}
                    />
                    {errors.plant_code && <div className="mat-error">{errors.plant_code.message}</div>}
                  </div>
                ))}

                {isCustomerId && (
                  <>
                    <div className={`form_field ${errors.operation_name ? 'error-outline' : ''}`}>
                      <TextField
                        {...register("operation_name", {
                          required: "Operation Name is required",
                          maxLength: {
                            value: 100,
                            message: "Maximum length is 100 characters"
                          }
                        })}
                        label="Operation Name"
                        type="text"
                        name="operation_name"
                        value={shiftForm.operation_name || ""}
                        onChange={handleOperationNameChange}
                        error={!!errors.operation_name}
                        InputLabelProps={{
                          required: true,
                          sx: { color: 'black', '&.Mui-focused': { color: 'orange' } },
                        }}
                        inputProps={{ maxLength: 100 }}
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: 'black' },
                            '&:hover fieldset': { borderColor: 'black' },
                            '&.Mui-focused fieldset': { borderColor: 'orange' },
                            '& .MuiOutlinedInput-input': { color: 'black' },
                            '&.Mui-focused .MuiOutlinedInput-input': { caretColor: 'orange' },
                          },
                        }}
                      />
                      {errors.operation_name && <div className="mat-error">{errors.operation_name.message}</div>}
                    </div>
                    <div className={`form_field ${errors.operation_number || operationNumberError ? 'error-outline' : ''}`}>
                      <TextField
                        {...register("operation_number", {
                          required: "Operation Number is required",
                          maxLength: {
                            value: 100,
                            message: "Maximum length is 100 characters"
                          }
                        })}
                        label="Operation Number"
                        type="text"
                        name="operation_number"
                        value={shiftForm.operation_number || ""}
                        onChange={handleFormChange}
                        error={!!errors.operation_number || !!operationNumberError}
                        InputLabelProps={{
                          required: true,
                          sx: { color: 'black', '&.Mui-focused': { color: 'orange' } },
                        }}
                        inputProps={{ maxLength: 100 }}
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: 'black' },
                            '&:hover fieldset': { borderColor: 'black' },
                            '&.Mui-focused fieldset': { borderColor: 'orange' },
                            '& .MuiOutlinedInput-input': { color: 'black' },
                            '&.Mui-focused .MuiOutlinedInput-input': { caretColor: 'orange' },
                          },
                        }}
                      />
                      {errors.operation_number && <div className="mat-error">{errors.operation_number.message}</div>}
                      {operationNumberError && <div className="mat-error">{operationNumberError}</div>}
                    </div>
                  </>
                )}
                {(cleanCustomerId(customerId) === window._env_.ATECH_CUSTOMER_ID || cleanCustomerId(customerId) === window._env_.HITECH_CUSTOMER_ID) && (<div className={`form_field ${errors.operation_type ? 'error-outline' : ''}`}>
                  <CustomDaySelect
                    {...register("operation_type", { required: "Operation type is required" })}
                    onBlur={() => trigger('operation_type')}
                    name="operation_type"
                    value={shiftForm.operation_type}
                    onChange={handleFormChange}
                    label="Operation Type"
                    required={true}
                    options={[
                      { value: 'VMC', label: 'VMC' },
                      { value: 'HMC', label: 'HMC' },
                      { value: 'CNC', label: 'CNC' },
                      { value: 'TC', label: 'TC' },
                    ]}
                    error={!!errors.operation_type}
                    sx={{
                      '& .MuiInputLabel-root': {
                        top: '-6px',
                        backgroundColor: '#ededed',
                        padding: '0 4px',
                      },
                    }}
                  />
                  {errors.operation_type && <div className="mat-error">{errors.operation_type.message}</div>}
                </div>)}
                {/* Operation Number Field 
                <div className={`form_field ${errors.operation_number ? 'error-outline' : ''}`}>
                  <TextField
                   inputRef={modeSelectRef}
                     {...register("operation_number", {
                      required: "Mould Number is required",
                      maxLength: {
                        value: 100,
                        message: "Maximum length is 100 characters"
                      },
                      setValueAs: v => v === "" ? "" : Number(v), // Converts to number or empty
                      validate: value => {
                        if (isNaN(value)) return "Mould Number must be a number";
                        if (value <= 0) return "Mould Number must be greater than 0";
                        return true;
                      }
                    })}
                    label="Mould Number"
                    type="number"
                    name="operation_number"
                    value={shiftForm.operation_number}
                    onChange={handleFormChange}
                    error={!!errors.operation_number}
                    inputProps={{ maxLength: 100 }}
                    InputLabelProps={{
                      required: true, 
                      sx: {
                        color: 'black',
                        '&.Mui-focused': {
                          color: 'orange',
                        },
                      },
                    }}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: 'black',
                        },
                        '&:hover fieldset': {
                          borderColor: 'black',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'orange',
                        },
                        '& .MuiOutlinedInput-input': {
                          color: 'black',
                        },
                        '&.Mui-focused .MuiOutlinedInput-input': {
                          caretColor: 'orange',
                        },
                        '&::placeholder': {
                          color: 'black',
                          opacity: 1,
                        },
                      },
                    }}
                  />
                  {errors.operation_number && <div className="mat-error">{errors.operation_number.message}</div>}
                </div>   */}

                {/* Operation Name Field 
             
                 <div className={`form_field  ${errors.operation_name ? 'error-outline' : ''}`}>
                  <TextField
                    {...register("operation_name", { required: "Mould Name is required",
                      maxLength: {
                        value: 100,
                        message: "Maximum length is 100 characters"
                      },
                      validate: value =>
                      /^[a-zA-Z0-9\s]*$/.test(value) || "Special characters are not allowed"
                     })}
                    onBlur={() => trigger('operation_name')}
                    label="Mould Name"
                    type="text"
                    name="operation_name"
                    value={shiftForm.operation_name}
                    onChange={handleFormChange}
                    error={!!errors.operation_name}
                    inputProps={{ maxLength: 100 }}
                    InputLabelProps={{
                      required: true, 
                      sx: {
                        color: 'black',
                        '&.Mui-focused': {
                          color: 'orange',
                        },
                      },
                    }}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: 'black',
                        },
                        '&:hover fieldset': {
                          borderColor: 'black',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'orange',
                        },
                        '& .MuiOutlinedInput-input': {
                          color: 'black',
                        },
                        '&.Mui-focused .MuiOutlinedInput-input': {
                          caretColor: 'orange',
                        },
                        '&::placeholder': {
                          color: 'black',
                          opacity: 1,
                        },
                      },
                    }}
                  />
                 {errors.operation_name && (
  <div className="mat-error">{errors.operation_name.message}</div>
)}
                </div>  */}



                {cleanCustomerId(customerId) === window._env_.GPLAST_CUSTOMER_ID && (
                  <>
                    <div className={`form_field ${errors.item_code ? 'error-outline' : ''}`}>
                      <TextField
                        {...register("item_code", {
                          required: "Item Code is required",
                          maxLength: {
                            value: 100,
                            message: "Maximum length is 100 characters",
                          },
                        })}
                        label="Item Code"
                        type="text"
                        name="item_code"
                        disabled
                        value={shiftForm.item_code || ""}
                        onChange={handleFormChange}
                        error={!!errors.item_code}
                        InputLabelProps={{
                          required: true,
                          sx: {
                            color: "black",
                            "&.Mui-focused": { color: "orange" },
                          },
                        }}
                        inputProps={{ maxLength: 100 }}
                        fullWidth
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": { borderColor: "black" },
                            "&:hover fieldset": { borderColor: "black" },
                            "&.Mui-focused fieldset": { borderColor: "orange" },
                            "& .MuiOutlinedInput-input": { color: "black" },
                            "&.Mui-focused .MuiOutlinedInput-input": { caretColor: "orange" },
                            "&::placeholder": { color: "black", opacity: 1 },
                          },
                        }}
                      />
                      {errors.item_code && <div className="mat-error">{errors.item_code.message}</div>}
                    </div>
                    <div className={`form_field ${errors.process_name ? "error-outline" : ""}`}>
                      <CustomDaySelect
                        {...register("process_name", { required: "Process Name is required" })}
                        onBlur={() => trigger("process_name")}
                        name="process_name"
                        disabled
                        value={shiftForm.process_name || ""}
                        onChange={handleFormChange}
                        label="Process Name"
                        required={true}
                        options={processGroupOptions}
                        error={!!errors.process_name}
                      />
                      {errors.process_name && (
                        <div className="mat-error">{errors.process_name.message}</div>
                      )}
                    </div>
                    <div className={`form_field ${errors.operation_number || operationNumberError ? 'error-outline' : ''}`}>
                      <TextField
                        {...register("operation_number", {
                          required: "Operation Number is required",
                          maxLength: {
                            value: 100,
                            message: "Maximum length is 100 characters",
                          },
                        })}
                        label="Operation Number"
                        type="text"
                        name="operation_number"
                        value={shiftForm.operation_number || ""}
                        onChange={handleFormChange}
                        error={!!errors.operation_number || !!operationNumberError}
                        InputLabelProps={{
                          required: true,
                          sx: {
                            color: "black",
                            "&.Mui-focused": { color: "orange" },
                          },
                        }}
                        inputProps={{ maxLength: 100 }}
                        fullWidth
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": { borderColor: "black" },
                            "&:hover fieldset": { borderColor: "black" },
                            "&.Mui-focused fieldset": { borderColor: "orange" },
                            "& .MuiOutlinedInput-input": { color: "black" },
                            "&.Mui-focused .MuiOutlinedInput-input": { caretColor: "orange" },
                            "&::placeholder": { color: "black", opacity: 1 },
                          },
                        }}
                      />
                      {errors.operation_number && <div className="mat-error">{errors.operation_number.message}</div>}
                      {operationNumberError && <div className="mat-error">{operationNumberError}</div>}
                    </div>
                  </>
                )}

                {!isCustomerId && (<>
                <div className={`form_field  ${errors.factorval ? 'error-outline' : ''}`}>
                  <TextField
                    {...register("factorval", {
                      maxLength: {
                        value: 100,
                        message: "Maximum length is 100 characters"
                      },
                      setValueAs: v => v === "" ? "" : Number(v),
                      validate: value => {
                        if (value === "" || value === null || value === undefined) {
                          return "Factor is required";
                        }
                        if (isNaN(value)) {
                          return "Factor must be a number";
                        }
                        if (value <= 0) {
                          return "Factor must be greater than 0";
                        }
                        return true;
                      }
                    })}
                    onBlur={() => trigger('factorval')}
                    label="Factor Value"
                    type="number"
                    name="factorval"
                    value={shiftForm.factorval}
                    onChange={handleFormChange}
                    error={!!errors.factorval}
                    inputProps={{ maxLength: 100, min: 1 }}
                    InputLabelProps={{
                      required: true,
                      sx: {
                        color: 'black',
                        '&.Mui-focused': {
                          color: 'orange',
                        },
                      },
                    }}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: 'black',
                        },
                        '&:hover fieldset': {
                          borderColor: 'black',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'orange',
                        },
                        '& .MuiOutlinedInput-input': {
                          color: 'black',
                        },
                        '&.Mui-focused .MuiOutlinedInput-input': {
                          caretColor: 'orange',
                        },
                        '&::placeholder': {
                          color: 'black',
                          opacity: 1,
                        },
                      },
                    }}
                  />
                  {errors.factorval && <div className="mat-error">{errors.factorval.message}</div>} {/* Changed to div and mat-error */}
                </div>
                <div className={`form_field  ${errors.factor ? 'error-outline' : ''}`}>
                  <CustomDaySelect
                    {...register("factor", { required: "Factor is required" })}
                    onBlur={() => trigger('factor')}
                    ref={customDaySelectRef}
                    name="factor"
                    value={shiftForm.factor}
                    onChange={handleFormChange}
                    label="Select Factor"
                    inputProps={{ maxLength: 100 }}
                    required={true}
                    options={shiftsmodule}
                    error={!!errors.factor}
                  />
                  {errors.factor && <div className="mat-error">{errors.factor.message}</div>} {/* Changed to div and mat-error */}
                </div>
                </>)}
                {/* ===== Cycle Time ===== */}
                <div className={`form_field ${errors.cycle_time ? "error-outline" : ""}`}>
                  <DemoItem className="white-label">
                    <TimePicker
                      {...register("cycle_time", {
                        required: "Cycle Time is required",
                        validate: (value) => {
                          if (!value) return "Cycle Time is required";
                          if (!dayjs(value).isValid()) return "Invalid time format";
                          return true;
                        },
                      })}
                      onBlur={() => trigger("cycle_time")}
                      value={shiftForm.cycle_time}
                      onChange={(value) => handleTimeChange("cycle_time", value)}
                      views={["hours", "minutes", "seconds"]}
                      format="HH:mm:ss"
                      ampm={false}
                      label="Cycle Time *"
                      error={!!errors.cycle_time}
                      slotProps={{
                        textField: {
                          helperText: errors.cycle_time?.message,
                        },
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: errors.cycle_time ? "red" : "black",
                          },
                          "&:hover fieldset": { borderColor: "black" },
                          "&.Mui-focused fieldset": {
                            borderColor: errors.cycle_time ? "red" : "orange",
                          },
                          "& .MuiOutlinedInput-input": { color: "black" },
                          "&.Mui-focused .MuiOutlinedInput-input": { caretColor: "orange" },
                        },
                      }}
                    />
                  </DemoItem>
                </div>

                {/* ===== Handling Time ===== */}
                <div className={`form_field ${errors.handling_time ? "error-outline" : ""}`}>
                  <DemoItem>
                    <TimePicker
                      {...register("handling_time", {
                        required: "Handling Time is required",
                        validate: (value) => {
                          if (!value) return "Handling Time is required";
                          if (!dayjs(value).isValid()) return "Invalid time format";
                          return true;
                        },
                      })}
                      onBlur={() => trigger("handling_time")}
                      value={shiftForm.handling_time}
                      onChange={(value) => handleTimeChange("handling_time", value)}
                      views={["hours", "minutes", "seconds"]}
                      format="HH:mm:ss"
                      ampm={false}
                      label="Handling Time *"
                      error={!!errors.handling_time}
                      slotProps={{
                        textField: {
                          helperText: errors.handling_time?.message,
                        },
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: errors.handling_time ? "red" : "black",
                          },
                          "&:hover fieldset": { borderColor: "black" },
                          "&.Mui-focused fieldset": {
                            borderColor: errors.handling_time ? "red" : "orange",
                          },
                          "& .MuiOutlinedInput-input": { color: "black" },
                          "&.Mui-focused .MuiOutlinedInput-input": { caretColor: "orange" },
                        },
                      }}
                    />
                  </DemoItem>
                </div>

                {/* ===== Setup Time ===== */}
                <div className={`form_field ${errors.setupTime ? "error-outline" : ""}`}>
                  <DemoItem>
                    <TimePicker
                      {...register("setupTime", {
                        required: "Setup Time is required",
                        validate: (value) => {
                          if (!value) return "Setup Time is required";
                          if (!dayjs(value).isValid()) return "Invalid time format";
                          return true;
                        },
                      })}
                      onBlur={() => trigger("setupTime")}
                      value={shiftForm.setupTime}
                      onChange={(value) => handleTimeChange("setupTime", value)}
                      views={["hours", "minutes", "seconds"]}
                      format="HH:mm:ss"
                      ampm={false}
                      label="Setup Time *"
                      error={!!errors.setupTime}
                      slotProps={{
                        textField: {
                          helperText: errors.setupTime?.message,
                        },
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: errors.setupTime ? "red" : "black",
                          },
                          "&:hover fieldset": { borderColor: "black" },
                          "&.Mui-focused fieldset": {
                            borderColor: errors.setupTime ? "red" : "orange",
                          },
                          "& .MuiOutlinedInput-input": { color: "black" },
                          "&.Mui-focused .MuiOutlinedInput-input": { caretColor: "orange" },
                        },
                      }}
                    />
                  </DemoItem>
                </div>



                {/* Operation Number Field 
                <div className={`form_field ${errors.jobcard ? 'error-outline' : ''}`}>
                  <TextField
                    {...register("jobcard", { required: "Job Card is required",
                      maxLength: {
                        value: 100,
                        message: "Maximum length is 100 characters"
                      },
                      validate: value =>
                        /^[a-zA-Z0-9\s]*$/.test(value) || "Special characters are not allowed" })}
                    label="Raw Material"
                    type="text"
                    name="jobcard"
                    value={shiftForm.jobcard}
                    onChange={handleFormChange}
                    error={!!errors.jobcard}
                    inputProps={{ maxLength: 100}}
                    InputLabelProps={{
                      required: true, 
                      sx: {
                        color: 'black',
                        '&.Mui-focused': {
                          color: 'orange',
                        },
                      },
                    }}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: 'black',
                        },
                        '&:hover fieldset': {
                          borderColor: 'black',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'orange',
                        },
                        '& .MuiOutlinedInput-input': {
                          color: 'black',
                        },
                        '&.Mui-focused .MuiOutlinedInput-input': {
                          caretColor: 'orange',
                        },
                        '&::placeholder': {
                          color: 'black',
                          opacity: 1,
                        },
                      },
                    }}
                  />
                  {errors.jobcard && <div className="mat-error">{errors.jobcard.message}</div>}
                </div>  


                <div className={`form_field ${errors.drawingcode ? 'error-outline' : ''}`}>
                  <TextField
                    {...register("drawingcode", { required: "Drawingcode is required",
                      maxLength: {
                        value: 100,
                        message: "Maximum length is 100 characters"
                      },
                      validate: value => {
                        if (isNaN(value)) return "Drawing code  must be a number";
                        if (Number(value) <= 0) return "Drawing code must be greater than 0";
                        return true;
                      }
                     })}
                    label="Raw Material Code"
                    type="number"
                    name="drawingcode"
                    value={shiftForm.drawingcode}
                    onChange={handleFormChange}
                    error={!!errors.drawingcode}
                    inputProps={{ maxLength: 100, min: 1 }}
                    InputLabelProps={{
                      required: true, 
                      sx: {
                        color: 'black',
                        '&.Mui-focused': {
                          color: 'orange',
                        },
                      },
                    }}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: 'black',
                        },
                        '&:hover fieldset': {
                          borderColor: 'black',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'orange',
                        },
                        '& .MuiOutlinedInput-input': {
                          color: 'black',
                        },
                        '&.Mui-focused .MuiOutlinedInput-input': {
                          caretColor: 'orange',
                        },
                        '&::placeholder': {
                          color: 'black',
                          opacity: 1,
                        },
                      },
                    }}
                  />
                  {errors.drawingcode && <div className="mat-error">{errors.drawingcode.message}</div>}
                </div>  */}



                {/* Rest of the form fields remain the same */}
              </div>
            </LocalizationProvider>
            <div className="form-button text-right" align="end" style={{ marginRight: '10px' }}>
              <Button type="submit" variant="contained" className="filter_btn btn_orange" color="warning">
                Save
              </Button>
            </div>
            <br></br>
          </form>
        </div>
      </div>
    </Dialog>
  );
}